<template>
    <div
        v-if="isCountryAndPostalCode"
        class="space-y-6"
    >
        <participant-detail-section>
            {{ t('address.countryAndPostalCode') }}
        </participant-detail-section>

        <div class="flex max-w-md flex-col gap-6 md:ml-6">
            <div class="flex flex-col">
                <label class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span>
                    {{ t('address.country') }}
                </label>
                <prime-auto-complete
                    id="country"
                    v-model="selectedCountry"
                    :input-props="{ autocomplete: 'country' }"
                    :placeholder="t('address.country')"
                    option-label="name"
                    data-key="code"
                    :suggestions="filteredCountries"
                    dropdown
                    type="text"
                    :pt="{
                        input: {
                            class: invalidCountry
                                ? 'w-full border-2 border-red-500'
                                : 'w-full'
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                    @complete="searchCountry"
                />
                <div>
                    <small
                        v-if="invalidCountry"
                        id="country-help"
                        class="text-red-600"
                        >{{ t('address.invalidCountryWarning') }}</small
                    >
                </div>
            </div>

            <div class="flex flex-col">
                <label class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span>
                    {{ t('address.postalCode') }}
                </label>
                <input-text
                    id="postal_code"
                    :model-value="addressData.postal"
                    autocomplete="postal-code"
                    :placeholder="t('address.postalCode')"
                    type="text"
                    :pt="{
                        root: {
                            class: invalidPostal
                                ? 'border-2 border-red-500'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                    @update:model-value="updatePostal"
                />
                <div>
                    <small
                        v-if="invalidPostal"
                        id="postal-help"
                        class="text-red-600"
                        >{{ t('address.invalidPostalCodeWarning') }}</small
                    >
                </div>
            </div>
        </div>
    </div>

    <div v-if="isFullAddress">
        <participant-detail-section>{{
            t('address.address')
        }}</participant-detail-section>

        <div class="flex max-w-4xl flex-col gap-4 pt-6 md:ml-6">
            <div class="flex flex-col">
                <label class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span>
                    {{ t('address.country') }}
                </label>
                <prime-auto-complete
                    id="country"
                    v-model="selectedCountry"
                    :input-props="{ autocomplete: 'country' }"
                    :placeholder="t('address.country')"
                    option-label="name"
                    data-key="code"
                    :suggestions="filteredCountries"
                    dropdown
                    type="text"
                    :pt="{
                        input: {
                            class: invalidCountry
                                ? 'w-full border-2 border-red-500'
                                : 'w-full'
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                    @complete="searchCountry"
                />
                <small
                    v-if="invalidCountry"
                    id="country-help"
                    class="text-red-600"
                    >{{ t('address.invalidCountryWarning') }}</small
                >
            </div>

            <div class="flex flex-col">
                <label class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span>
                    {{ t('address.lineOne') }}
                </label>
                <input-text
                    id="line1"
                    :model-value="addressData.address1"
                    :placeholder="t('address.lineOne')"
                    type="text"
                    :pt="{
                        root: {
                            class: invalidAddress1
                                ? 'border-2 border-red-500'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                    @update:model-value="updateAddressLine1"
                />
                <small
                    v-if="invalidAddress1"
                    id="address1-help"
                    class="text-red-600"
                    >{{ t('address.streetAddressRequired') }}</small
                >
            </div>

            <div class="flex flex-col">
                <label class="text-sm text-gray-500">{{
                    t('address.lineTwo')
                }}</label>
                <input-text
                    id="line2"
                    :model-value="addressData.address2"
                    type="text"
                    :placeholder="t('address.lineTwo')"
                    @update:model-value="updateAddressLine2"
                />
            </div>

            <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div class="flex flex-col">
                    <label class="text-sm text-gray-500">
                        <span class="text-red-600">*&nbsp;</span>
                        {{ t('address.city') }}
                    </label>
                    <input-text
                        id="city"
                        :model-value="addressData.city"
                        type="text"
                        :placeholder="t('address.city')"
                        :pt="{
                            root: {
                                class: invalidCity
                                    ? 'border-2 border-red-500'
                                    : ''
                            }
                        }"
                        :pt-options="{ mergeSections: true, mergeProps: true }"
                        @update:model-value="updateCity"
                    />
                    <small
                        v-if="invalidCity"
                        id="city-help"
                        class="text-red-600"
                        >{{ t('address.cityRequired') }}</small
                    >
                </div>

                <div class="flex flex-col">
                    <label
                        class="text-sm text-gray-500"
                        for="state"
                    >
                        <span class="text-red-600">*&nbsp;</span>
                        {{
                            isUnitedStates
                                ? t('address.state')
                                : t('address.stateOrProvince')
                        }}
                    </label>
                    <prime-dropdown
                        v-if="isUnitedStates"
                        id="state"
                        :model-value="addressData.state"
                        option-label="name"
                        class="!w-full"
                        :placeholder="t('address.state')"
                        option-value="code"
                        :options="unitedStatesCodes"
                        :pt="{
                            root: {
                                class: invalidState
                                    ? 'border-2 border-red-500'
                                    : ''
                            }
                        }"
                        :pt-options="{ mergeSections: true, mergeProps: true }"
                        @update:model-value="updateState"
                    />
                    <input-text
                        v-else
                        id="state"
                        :model-value="addressData.state"
                        :placeholder="t('address.stateOrProvince')"
                        type="text"
                        :pt="{
                            root: {
                                class: invalidState
                                    ? 'border-2 border-red-500'
                                    : ''
                            }
                        }"
                        :pt-options="{ mergeSections: true, mergeProps: true }"
                        @update:model-value="updateState"
                    />
                    <small
                        v-if="invalidState"
                        id="state-help"
                        class="text-red-600"
                        >{{
                            isUnitedStates
                                ? t('address.stateRequired')
                                : t('address.stateOrProvinceRequired')
                        }}</small
                    >
                </div>

                <div class="flex flex-col">
                    <label class="text-sm text-gray-500">
                        <span class="text-red-600">*&nbsp;</span>
                        {{ t('address.postalCode') }}
                    </label>
                    <input-text
                        id="postal"
                        :model-value="addressData.postal"
                        type="text"
                        :placeholder="t('address.postalCode')"
                        :pt="{
                            root: {
                                class: invalidPostal
                                    ? 'border-2 border-red-500'
                                    : ''
                            }
                        }"
                        :pt-options="{ mergeSections: true, mergeProps: true }"
                        @update:model-value="updatePostal"
                    />
                    <small
                        v-if="invalidPostal"
                        id="postal-help"
                        class="text-red-600"
                        >{{ t('address.invalidPostalCodeWarning') }}</small
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const AddressInputPlanFragment = graphql(/** GraphQL */ `
    fragment AddressInputPlanFragment on Plan {
        terms {
            addressRequirement
        }
    }
`)
</script>

<script setup lang="ts">
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import { computed, onMounted, ref, watch } from 'vue'
import InputText from 'primevue/inputtext'
import ParticipantDetailSection from '~/components/PurchaseContract/ParticipantDetails/ParticipantDetailSection.vue'
import PrimeAutoComplete from 'primevue/autocomplete'
import type { AddressData } from '~/composables/addressData'
import countryCodes from 'assets/country-code.json'
import unitedStatesCodes from 'assets/us-code.json'
import PrimeDropdown from 'primevue/dropdown'
import { useShowAlert } from '~/composables/use-show-alert'
import { useI18n } from 'vue-i18n'
import { useActiveFacility } from '#imports'

const props = defineProps<{
    plan: FragmentType<typeof AddressInputPlanFragment>
    addressData: AddressData
    showErrorMessages: boolean
}>()

const emit = defineEmits<{
    (e: 'update:addressData', newAddress: AddressData): void
}>()

const { t } = useI18n()
const planData = computed(() =>
    getFragmentData(AddressInputPlanFragment, props.plan)
)

// On mounted we either set the selected country to the existing one from props
// Or if no existing country is given we default to the US
onMounted(() => {
    if (props.addressData.country !== '') {
        const propsSelectedCountry = countryOptions.find(
            option => option.code === props.addressData.country
        )
        if (propsSelectedCountry === undefined) {
            throw new Error('Error finding country code.')
        }
        selectedCountry.value = propsSelectedCountry
    } else {
        updateValue('country', defaultCountryOption.value.code)
    }
})

function updateValue<T extends keyof AddressData>(
    key: T,
    value: AddressData[T]
) {
    const newAddress = {
        ...props.addressData,
        [key]: value
    }
    newAddress.invalid = addressIsInvalid(newAddress)

    emit('update:addressData', newAddress)
}

function addressIsInvalid(address: AddressData) {
    if (address.country === '' || address.postal === '') {
        return true
    } else if (planData.value.terms.addressRequirement === 'FULL') {
        if (
            address.address1 === '' ||
            address.city === '' ||
            address.state === '' ||
            address.country === '' ||
            address.postal === ''
        ) {
            return true
        }
    }

    return false
}

const isCountryAndPostalCode = computed(() => {
    return planData.value.terms.addressRequirement === 'COUNTRY_AND_POSTAL_CODE'
})
const isFullAddress = computed(() => {
    return planData.value.terms.addressRequirement === 'FULL'
})

const isUnitedStates = computed(() => props.addressData.country === 'US')
const unitedStatesOption = { code: 'US', name: 'United States' }
const countryOptions = [
    unitedStatesOption,
    ...(countryCodes as unknown as { code: string; name: string }[]).filter(
        option => option.code !== 'US'
    )
]
const activeFacility = useActiveFacility()
const defaultCountryOption = computed(() => {
    const facilityCountry = countryOptions.find(
        country => country.code === activeFacility.value.country
    )
    if (facilityCountry === undefined) {
        throw new Error('Cannot find facility setting country')
    }
    return facilityCountry
})

const filteredCountries = ref<{ code: string; name: string }[]>([])
const selectedCountry = ref<{ code: string; name: string } | string>(
    defaultCountryOption.value
)

const showAlert = useShowAlert()
const invalidAddress1 = computed(() => {
    return props.showErrorMessages && props.addressData.address1 === ''
})
const invalidCity = computed(() => {
    return props.showErrorMessages && props.addressData.city === ''
})
const invalidState = computed(() => {
    return props.showErrorMessages && props.addressData.state === ''
})
const invalidPostal = computed(() => {
    return props.showErrorMessages && props.addressData.postal === ''
})
const invalidCountry = computed(() => {
    return props.showErrorMessages && props.addressData.country === ''
})

function searchCountry(event: { query: string }): void {
    const query = event.query.trim().toLowerCase()
    if (query.length > 0) {
        const terms = query.split(' ')
        filteredCountries.value = countryOptions.filter(option => {
            return terms.every(
                term =>
                    option.name.toLowerCase().includes(term) ||
                    option.code.toLowerCase().includes(term)
            )
        })
    } else {
        filteredCountries.value = [...countryOptions]
    }
}

watch(selectedCountry, (newValue: { code: string; name: string } | string) => {
    // If newValue is an object (they clicked one of the options in the list)
    if (typeof newValue === 'object') {
        if (newValue.code.length > 64) {
            showAlert('Error', t('address.countryCodeLengthWarning'))
            updateValue('country', '')
        } else {
            updateValue('country', newValue.code)
        }
    }
    // Else newValue is a string (it came from typing)
    else {
        // If they typed one of the country names (case-insensitive)
        // Then we select that country for them and emit
        const country = countryOptions.find(
            option => option.name.toLowerCase() === newValue.toLowerCase()
        )
        if (country === undefined) {
            updateValue('country', '')
        } else {
            selectedCountry.value = country
            updateValue('country', country.code)
        }
    }
})

function updatePostal(newValue: string) {
    if (newValue.length > 64) {
        showAlert('Error', t('address.postalCodeLengthWarning'))
        return
    }
    updateValue('postal', newValue)
}

function updateAddressLine1(newValue: string) {
    if (newValue.length > 128) {
        showAlert('Error', t('address.lineOneLengthWarning'))
        return
    }
    updateValue('address1', newValue)
}

function updateAddressLine2(newValue: string) {
    if (newValue.length > 128) {
        showAlert('Error', t('address.lineTwoLengthWarning'))
        return
    }
    updateValue('address2', newValue)
}

function updateCity(newValue: string) {
    if (newValue.length > 64) {
        showAlert('Error', t('address.cityLengthWarning'))
        return
    }
    updateValue('city', newValue)
}

function updateState(newValue: string) {
    if (newValue.length > 64) {
        showAlert('Error', t('address.stateLengthWarning'))
        return
    }
    updateValue('state', newValue)
}
</script>
